import { ApolloError } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { fromPromise } from '@apollo/client/link/utils';
import { defaultLocalStorageAdapter } from '@hover/graphql';

import type { Jwt } from 'src/shared/graphql/generated';
import { mutations } from 'src/shared/graphql/requests';
import { ChannelEnum, openChannel } from 'src/shared/helpers/webviewChannel';

import { client as apolloClient, isAuthorizationError } from '../..';

import { link as queueLink } from './queueLink';

interface RefreshLinkOptions {
  onUnauthenticated?: () => void;
}

const missingRefreshToken = new Error('Missing refreshToken');
const missingJwtError = new Error('Missing Jwt');

const getNewToken = async () => {
  const refreshToken = await defaultLocalStorageAdapter.getRefreshToken();
  if (refreshToken === null) {
    throw missingRefreshToken;
  }
  const refreshTokenResponse = await mutations.refreshToken(apolloClient, {
    refreshToken,
  });

  const jwt = refreshTokenResponse.data?.refreshJwt;
  if (!jwt) {
    throw missingJwtError;
  }

  return jwt;
};

const getNewTokenViaApp = async () => {
  const accessToken = await defaultLocalStorageAdapter.getAccessToken();
  const channel = openChannel<any, Jwt>(ChannelEnum.SHARE_HOVER_AUTHORIZATION);

  channel.send({ expired: accessToken });

  return channel.receive;
};

const updateToken = async (jwt: Jwt) => {
  const { accessToken, refreshToken } = jwt;

  await defaultLocalStorageAdapter.setAccessToken(accessToken);
  if (refreshToken) {
    await defaultLocalStorageAdapter.setRefreshToken(refreshToken);
  }

  return jwt;
};

const createRefreshLink = ({ onUnauthenticated }: RefreshLinkOptions) => {
  let tokenRequest: Promise<Jwt> | null = null;

  const refreshToken = async () => {
    queueLink.close();

    try {
      try {
        tokenRequest = getNewTokenViaApp();
      } catch (e) {
        tokenRequest = getNewToken();
      }

      return await updateToken(await tokenRequest);
    } catch (error) {
      onUnauthenticated?.();
      return null;
    } finally {
      tokenRequest = null;
      queueLink.open();
    }
  };

  return onError(({ graphQLErrors, operation, forward }) => {
    if (graphQLErrors && graphQLErrors.find(isAuthorizationError)) {
      return fromPromise(tokenRequest || refreshToken()).flatMap(jwt => {
        // NOTE: we need to throw an `ApolloError` here to ensure that
        // unauthorized requests fail the same way that they would without the
        // JWT refresh behavior. If we don't, then Apollo Client wraps the error
        // in an `ApolloError` as `error.networkError` which is incorrect.
        if (!jwt) throw new ApolloError({ graphQLErrors });

        return forward(operation);
      });
    }

    return undefined;
  });
};

export { createRefreshLink, RefreshLinkOptions };
